import { LoggingService } from '@app/core/services/logging/logging.service';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { LocationModel, PostLocationModel } from '@app/core/models';
import { LocationRepository } from '@app/store/location.repository';
import { Observable, from, of, switchMap, tap } from 'rxjs';
import { environment } from 'src/environments/environment';
import { FirebaseAuthenticationService } from '../firebase/firebase-authentication/firebase-authentication.service';
import { RouterService } from '../router/router.service';

/**
 * Service responsible for handling location functionality.
 */
@Injectable({
  providedIn: 'root',
})
export class LocationService {
  constructor(
    private _http: HttpClient,
    private _locationRepo: LocationRepository,
    private _routerService: RouterService,
    private authService: FirebaseAuthenticationService,
  ) {}

  /**
   * Retrieves the user locations from the server.
   * @returns An observable that emits the user locations.
   */
  getAll(): Observable<LocationModel[]> {
    LoggingService.log('getAll locations');
    return this._http
      .get<LocationModel[]>(`${environment.api.serverUrl}/users/locations`)
      .pipe(
        switchMap((locations) => {
          LoggingService.log('locations fetched:', locations);
          this._locationRepo.upsertLocations(locations);
          return of(locations);
        }),
      );
  }

  create(location: PostLocationModel): Observable<LocationModel> {
    LoggingService.log('Creating location with data:', location);
    return from(this.authService.getIdToken({ forceRefresh: false })).pipe(
      switchMap((data) => {
        LoggingService.log('Got auth token:', data.token);
        return this._http.post<LocationModel>(
          `${environment.api.serverUrl}/users/locations`,
          location,
          {
            headers: {
              Authorization: `Bearer ${data.token}`,
              'Content-Type': 'application/json',
            },
          },
        );
      }),
      tap({
        next: (location) => {
          LoggingService.log('Location created successfully:', location);
          this._locationRepo.addLocation(location);
        },
        error: (error) =>
          LoggingService.error('Error creating location:', error),
      }),
    );
  }

  setDefaultLocation(location: LocationModel): Observable<LocationModel[]> {
    return from(this.authService.getIdToken({ forceRefresh: false })).pipe(
      switchMap((data) => {
        return this._http.post<LocationModel[]>(
          `${environment.api.serverUrl}/users/locations/${location._id}/default`,
          {},
          {
            headers: {
              Authorization: `Bearer ${data.token}`,
              'Content-Type': 'application/json',
            },
          },
        );
      }),
      tap((locations) => {
        this._locationRepo.upsertLocations(locations);
      }),
    );
  }

  getTimezone(location: { lat: number; lng: number }): Observable<string> {
    return this._http.get<string>(
      `${environment.api.serverUrl}/users/locations/timezone`,
      { params: location },
    );
  }
}
